import { useEffect, useState } from "react";
import { Bell, BellOff, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export default function NotificationPreferences({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [supported, setSupported] = useState(false);
  const [endpoint, setEndpoint] = useState(null);
  const [prefs, setPrefs] = useState({ subscribed: false, enabled: true, categories: [], all_categories: [], flight_alerts: false });
  const [allCats, setAllCats] = useState([]);

  useEffect(() => {
    (async () => {
      const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
      setSupported(ok);
      if (!ok) { setLoading(false); return; }
      try {
        const cats = await api.get("/categories");
        setAllCats(cats.data || []);
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) {
          setPrefs((p) => ({ ...p, subscribed: false, all_categories: (cats.data || []).map((c) => c.slug) }));
          setLoading(false);
          return;
        }
        setEndpoint(sub.endpoint);
        const r = await api.get("/push/preferences", { params: { endpoint: sub.endpoint } });
        setPrefs({
          subscribed: r.data.subscribed,
          enabled: r.data.enabled ?? true,
          categories: r.data.categories || (cats.data || []).map((c) => c.slug),
          all_categories: r.data.all_categories || (cats.data || []).map((c) => c.slug),
          flight_alerts: r.data.flight_alerts ?? false,
        });
      } catch (err) {
        // sin sub o sin permiso → mostramos CTA de activación
        console.warn("notification prefs: no subscription yet", err?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permiso denegado");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const r = await api.get("/push/public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(r.data.public_key),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setEndpoint(json.endpoint);
      const p = await api.get("/push/preferences", { params: { endpoint: json.endpoint } });
      setPrefs({
        subscribed: true,
        enabled: p.data.enabled ?? true,
        categories: p.data.categories || [],
        all_categories: p.data.all_categories || [],
        flight_alerts: p.data.flight_alerts ?? false,
      });
      toast.success("Notificaciones activadas");
    } catch (err) {
      toast.error("No se pudieron activar las notificaciones");
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    if (!window.confirm("¿Querés desactivar y desuscribirte por completo?")) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      setPrefs((p) => ({ ...p, subscribed: false }));
      setEndpoint(null);
      toast.success("Te desuscribiste de las notificaciones");
    } catch (err) {
      toast.error("Error al desuscribir");
    } finally {
      setBusy(false);
    }
  };

  const savePrefs = async (next) => {
    if (!endpoint) return;
    try {
      await api.put("/push/preferences", { endpoint, ...next });
    } catch (err) {
      toast.error("No se pudo guardar");
    }
  };

  const toggleMaster = async () => {
    const nextEnabled = !prefs.enabled;
    setPrefs((p) => ({ ...p, enabled: nextEnabled }));
    await savePrefs({ enabled: nextEnabled });
    toast.success(nextEnabled ? "Notificaciones activadas" : "Notificaciones pausadas");
  };

  const toggleCategory = async (slug) => {
    const set = new Set(prefs.categories);
    if (set.has(slug)) set.delete(slug); else set.add(slug);
    const next = Array.from(set);
    setPrefs((p) => ({ ...p, categories: next }));
    await savePrefs({ categories: next });
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center p-0 md:p-4"
      style={{ background: "rgba(0,0,0,0.65)" }}
      onClick={onClose}
      data-testid="notif-prefs-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-lg rounded-t-2xl md:rounded-md border shadow-2xl overflow-hidden"
        style={{ background: "var(--bg)", borderColor: "var(--border)" }}
        data-testid="notif-prefs-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <Bell size={20} className="accent-text" />
            <h2 className="font-headline uppercase text-lg tracking-wider">Notificaciones</h2>
          </div>
          <button onClick={onClose} data-testid="notif-prefs-close" className="p-1 hover:opacity-70"><X size={22} /></button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {!supported ? (
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Tu navegador no soporta notificaciones push. Probá desde Chrome, Firefox o Safari 16.4+.
            </p>
          ) : loading ? (
            <div className="py-8 flex items-center justify-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
              <Loader2 size={16} className="animate-spin" /> Cargando preferencias...
            </div>
          ) : !prefs.subscribed ? (
            <div className="text-center py-4">
              <BellOff size={42} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
              <p className="text-sm mb-1 font-semibold">Aún no recibís notificaciones</p>
              <p className="text-xs mb-5" style={{ color: "var(--text-muted)" }}>
                Activá para enterarte al toque de las breaking news y noticias destacadas.
              </p>
              <button
                onClick={subscribe}
                disabled={busy}
                data-testid="notif-prefs-activate"
                className="btn-accent disabled:opacity-50"
              >
                {busy ? "Activando..." : "Activar notificaciones"}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Master toggle */}
              <div
                className="flex items-center justify-between p-4 border rounded-sm"
                style={{ borderColor: "var(--border)", background: "var(--surface)" }}
              >
                <div className="min-w-0 pr-4">
                  <p className="font-bold text-sm">Recibir notificaciones</p>
                  <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>
                    {prefs.enabled
                      ? "Estás recibiendo notificaciones. Podés pausarlas sin desuscribirte."
                      : "Pausadas. No vas a recibir notificaciones hasta que las actives."}
                  </p>
                </div>
                <button
                  onClick={toggleMaster}
                  data-testid="notif-prefs-master-toggle"
                  className="relative inline-flex h-7 w-12 shrink-0 rounded-full transition"
                  style={{ background: prefs.enabled ? "var(--accent)" : "var(--text-subtle)" }}
                  aria-pressed={prefs.enabled}
                >
                  <span
                    className="absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform"
                    style={{ transform: prefs.enabled ? "translateX(20px)" : "translateX(0)" }}
                  />
                </button>
              </div>

              {/* Categories */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
                  Secciones que querés recibir
                </p>
                <div className="space-y-2">
                  {allCats.map((c) => {
                    const active = prefs.categories.includes(c.slug);
                    return (
                      <label
                        key={c.slug}
                        className="flex items-center justify-between p-3 border rounded-sm cursor-pointer hover:accent-border transition"
                        style={{ borderColor: "var(--border)", opacity: prefs.enabled ? 1 : 0.5 }}
                        data-testid={`notif-prefs-cat-${c.slug}`}
                      >
                        <span className="text-sm font-semibold">{c.name}</span>
                        <input
                          type="checkbox"
                          checked={active}
                          disabled={!prefs.enabled}
                          onChange={() => toggleCategory(c.slug)}
                          className="w-5 h-5 accent-current"
                          style={{ accentColor: "var(--accent)" }}
                        />
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] mt-3" style={{ color: "var(--text-muted)" }}>
                  Las breaking news llegan a todo el mundo aunque hayas desactivado la sección. Podés pausarlas con el switch de arriba.
                </p>
              </div>

              {/* Flight alerts (Bolivia) */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
                  Alertas especiales
                </p>
                <label
                  className="flex items-center justify-between p-3 border rounded-sm cursor-pointer hover:accent-border transition"
                  style={{ borderColor: "var(--border)", opacity: prefs.enabled ? 1 : 0.5 }}
                  data-testid="notif-prefs-flights"
                >
                  <div className="min-w-0 pr-4">
                    <p className="text-sm font-semibold">✈️ Vuelos cancelados o demorados</p>
                    <p className="text-[11px] leading-tight" style={{ color: "var(--text-muted)" }}>
                      Recibí un aviso cuando un vuelo de Bolivia (BoA, EcoJet, etc) sea cancelado o demorado significativamente.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={prefs.flight_alerts}
                    disabled={!prefs.enabled}
                    onChange={async () => {
                      const next = !prefs.flight_alerts;
                      setPrefs((p) => ({ ...p, flight_alerts: next }));
                      await savePrefs({ flight_alerts: next });
                      toast.success(next ? "Alertas de vuelos activadas" : "Alertas de vuelos desactivadas");
                    }}
                    className="w-5 h-5"
                    style={{ accentColor: "var(--accent)" }}
                  />
                </label>
              </div>

              <button
                onClick={unsubscribe}
                disabled={busy}
                data-testid="notif-prefs-unsubscribe"
                className="text-xs font-semibold underline opacity-70 hover:opacity-100"
              >
                Desuscribirme por completo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
